import { InvoiceDoc } from 'app/core/_base/crud/models/invoice-doc.model';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

@Injectable({
    providedIn: 'root'
})
export class InvoiceDocService {

    /**
     * Url to upload an invoicing attached document
     */
    private API_UPLOAD_ATTACHED_DOCUMENT_LETTER_URL = environment.ipserver + 'o2c-backend/private/invoiceDocs/register';

    /**
     * Url to get a list of invoicing attached document
     */
    private API_ATTACHED_DOCUMENT_LETTER_LIST_URL = environment.ipserver + 'o2c-backend/private/invoiceDocs/list';

    /**
     * Url to generate a pdf for an invoicing attached document
     */
    private API_GENERATE_ATTACHED_DOCUMENT_LETTER_URL = environment.ipserver + 'o2c-backend/private/invoiceDocs/generate';

    /**
     * Url to delete an invoicing attached document
     */
    private API_DELETE_ATTACHED_DOCUMENT_LETTER_URL = environment.ipserver + 'o2c-backend/private/invoiceDocs/delete';

    constructor(
        private httpClient: HttpClient
    ) { }

    /**
     * Generates a report with credits registered in DB
     * @author Castor Transformación Digital - Alvaro Felipe Garcia Mendez
     * @since 2020-01-14
     * @param file: File to upload
     * @param startDate: text with the initial date of the range
     * @param endDate: text with the final date of the range
     * @param allCustomers: define if the invoice support doc was sended to all customers
     * @return Observable with a text that contains a message of confirmation
     */
    public uploadLetter(file: File, startDate: string, endDate: string, allCustomers: boolean): Observable<any> {
        const formData = new FormData();
        formData.append('file', file);
        const paramsForReq = new HttpParams()
            .set('startDate', startDate)
            .set('endDate', endDate)
            .set('allCustomers', (allCustomers) ? 'Y' : 'N');
        const type = 'text' as 'json';
        return this.httpClient.post(this.API_UPLOAD_ATTACHED_DOCUMENT_LETTER_URL, formData, {
            params: paramsForReq,
            responseType: type
        });
    }

    /**
     * Generates a report with credits registered in DB
     * @author Castor Transformación Digital - Alvaro Felipe Garcia Mendez
     * @since 2020-01-14
     * @return Observable with an array that contains an invoicing attached documents
     */
    public list(): Observable<Array<InvoiceDoc>> {
        return this.httpClient.get(this.API_ATTACHED_DOCUMENT_LETTER_LIST_URL)
            .pipe(
                map((value: Array<InvoiceDoc>) => {
                    value.forEach((elem: InvoiceDoc) => {
                        elem.name = 'invoiceDoc_' + elem.startDate.replace(/[\/]+/g, '') + '.pdf';
                    });
                    return value;
                })
            );
    }

    /**
     * Generates an invoicing attach document registered in DB
     * @author Castor Transformación Digital - Alvaro Felipe Garcia Mendez
     * @since 2020-01-14
     * @param invoiceDoc: invoicing attached document to generate the doc
     * @return Observable with a text that contains a pdf un base 64
     */
    public generate(invoiceDoc: InvoiceDoc): Observable<any> {
        const type = 'text' as 'json';
        return this.httpClient.post(this.API_GENERATE_ATTACHED_DOCUMENT_LETTER_URL, invoiceDoc, { responseType: type });
    }

    /**
     * Delete an attach document
     * @author Castor Transformación Digital - Fabio Oquendo
     * @since 2020-06-23
     * @param invoiceDocIdentifier: Identifier of invoicing attached document
     */
    public delete(invoiceDocIdentifier): Observable<string> {
        const paramsForPost = new HttpParams().set('invoiceDocIdentifier', invoiceDocIdentifier);
        const type = 'text' as 'json';
        return this.httpClient.delete<string>(this.API_DELETE_ATTACHED_DOCUMENT_LETTER_URL, { params: paramsForPost, responseType: type });
    }
}
